/**
 * Copyright (c) 2010-2026 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { PointerEventCoordinate } from './interfaces';
import { Observable, Observer, Subject } from 'rxjs';
import { ElementRef, NgZone, Renderer2 } from '@angular/core';
import { finalize, share } from 'rxjs/operators';

export class PointerEventListeners {
    private static instance: PointerEventListeners; // tslint:disable-line
    public pointerDown$: Observable<PointerEventCoordinate>;
    public pointerMove$: Observable<PointerEventCoordinate>;
    public pointerUp$: Observable<PointerEventCoordinate>;
    private pointerDownSubject = new Subject<PointerEventCoordinate>();
    private pointerMoveSubject = new Subject<PointerEventCoordinate>();
    private pointerUpSubject = new Subject<PointerEventCoordinate>();

    constructor(private renderer: Renderer2, private ngZone: NgZone) {
        this.pointerDown$ = this.pointerDownSubject.asObservable();
    }

    public initialize(el: ElementRef) {
        let unsubscribeMouseDown: () => void;
        let unsubscribeTouchStart: () => void;

        this.ngZone.runOutsideAngular(() => {
            unsubscribeMouseDown = this.renderer.listen('document', 'mousedown', (event: MouseEvent) => {
                // event.preventDefault();
                this.pointerDownSubject.next({ clientX: event.clientX, clientY: event.clientY, event });
            });

            unsubscribeTouchStart = this.renderer.listen('document', 'touchstart', (event: TouchEvent) => {
                // event.preventDefault();
                this.pointerDownSubject.next({
                    clientX: event.touches[0].clientX,
                    clientY: event.touches[0].clientY,
                    event,
                });
            });
        });

        this.pointerDown$.pipe(
            share(),
        );

        this.pointerMove$ = new Observable((observer: Observer<PointerEventCoordinate>) => {
            let unsubscribeMouseMove: () => void;
            let unsubscribeTouchMove: () => void;

            this.ngZone.runOutsideAngular(() => {
                unsubscribeMouseMove = this.renderer.listen('document', 'mousemove', (event: MouseEvent) => {
                    // event.preventDefault();
                    observer.next({ clientX: event.clientX, clientY: event.clientY, event });
                });

                unsubscribeTouchMove = this.renderer.listen('document', 'touchmove', (event: TouchEvent) => {
                    // event.preventDefault();
                    observer.next({
                        clientX: event.targetTouches[0].clientX,
                        clientY: event.targetTouches[0].clientY,
                        event,
                    });
                });
            });

            return () => {
                unsubscribeMouseMove();
                unsubscribeTouchMove();
            };
        }).pipe(
            share(),
        );

        /*
         let unsubscribeMouseMove: () => void;
         let unsubscribeTouchMove: () => void;

         this.zone.runOutsideAngular(() => {

         unsubscribeMouseMove = this.renderer.listen('document', 'mousemove', (event: MouseEvent) => {
         this.pointerMoveSubject.next({clientX: event.clientX, clientY: event.clientY, event});
         });

         unsubscribeTouchMove = this.renderer.listen('document', 'touchmove', (event: TouchEvent) => {
         event.preventDefault();
         this.pointerMoveSubject.next({
         clientX: event.targetTouches[0].clientX,
         clientY: event.targetTouches[0].clientY,
         event
         });
         });
         });

         this.pointerMove$.pipe(
         tag('pointer-move'),
         share(),
         );
         */

        this.pointerUp$ = new Observable((observer: Observer<PointerEventCoordinate>) => {
            let unsubscribeMouseUp: () => void;
            let unsubscribeTouchEnd: () => void;
            let unsubscribeTouchCancel: () => void;

            this.ngZone.runOutsideAngular(() => {
                unsubscribeMouseUp = this.renderer.listen('document', 'mouseup', (event: MouseEvent) => {
                    // event.preventDefault();
                    observer.next({
                        clientX: event.clientX,
                        clientY: event.clientY,
                        event,
                    });
                });

                unsubscribeTouchEnd = this.renderer.listen('document', 'touchend', (event: TouchEvent) => {
                    // event.preventDefault();
                    observer.next({
                        clientX: event.changedTouches[0].clientX,
                        clientY: event.changedTouches[0].clientY,
                        event,
                    });
                });

                unsubscribeTouchCancel = this.renderer.listen('document', 'touchcancel', (event: TouchEvent) => {
                    // event.preventDefault();
                    observer.next({
                        clientX: event.changedTouches[0].clientX,
                        clientY: event.changedTouches[0].clientY,
                        event,
                    });
                });
            });

            return () => {
                unsubscribeMouseUp();
                unsubscribeTouchEnd();
                unsubscribeTouchCancel();
            };
        }).pipe(
            share(),
        );
    }

    public static getInstance(renderer: Renderer2, zone: NgZone): PointerEventListeners {
        if (!PointerEventListeners.instance) {
            PointerEventListeners.instance = new PointerEventListeners(renderer, zone);
        }
        return PointerEventListeners.instance;
    }
}
